<script setup lang="ts">
import { Form, Field, ErrorMessage } from 'vee-validate'
import * as yup from 'yup'
import { v4 as uuidv4 } from 'uuid'
import RichTextEditor from '~/components/Form/RichTextEditor.vue'

const store = usePrestacaoDeContaStore()
const permissaoStore = usePermissaoStore()
const usuarioStore = useUsuarioStore()
const { modalEditarDespesa, modalConfigurarRecorrencia } = storeToRefs(store)
const arquivos = ref([])
const abaAtiva = ref('detalhes')
const despesasRecorrentes = ref([])
const despesasSelecionadasParaRemocao = ref([])
const carregandoRecorrencias = ref(false)
const adicionandoNovasRecorrencias = ref(false)
const arquivosRecorrencia = ref({}) // Para armazenar os arquivos de cada despesa recorrente
const despesaRecorrenteEmFoco = ref(null) // Despesa atual sendo editada ou recebendo anexos

const larkStore = useLarkStore()

// Data atual no formato YYYY-MM-DD para limitar o datepicker
const dataAtual = computed(() => {
  const hoje = new Date()
  return hoje.toISOString().split('T')[0]
})

// Data mínima permitida baseada na trava de competência
const dataMinima = computed(() => {
  try {
    // Admin sempre pode
    const isAdmin = usuarioStore.usuarioAtual.usuario?.admin === 'S'
    if (isAdmin) { return null }

    // Usuários com permissão de exclusão não são afetados pela trava
    const temPermissaoExcluir = usuarioStore.usuarioAtual.cartorio.permissoes
      .some(permissao => permissao.excluir === 1 && permissao.especialidade_nome === 'financeiro')

    if (temPermissaoExcluir) { return null }

    // Se trava desabilitada, não há restrição
    if (permissaoStore.permitirEdicaoCompetenciaFechada) { return null }

    // Se trava habilitada, não pode escolher datas de competências fechadas
    const hoje = new Date()
    const primeiroMesAtual = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
    return primeiroMesAtual.toISOString().split('T')[0]
  } catch (error) {
    console.warn('Erro ao calcular data mínima:', error)
    return null
  }
})

onMounted(async () => {
  await store.fetchCategorias()
  await store.fetchContas()
})

const existingFiles = computed(() => {
  return store.despesas.find(despesa => despesa.id === modalEditarDespesa.value.id)?.comprovantes
})

// Customização do método yup para números formatados
yup.addMethod(yup.number, 'formatted', function (message) {
  return this.transform((value, originalValue) => {
    console.log(value)
    const valorFormatado = originalValue ? Number(originalValue?.replace(',', '.')) : originalValue
    modalEditarDespesa.value.valor = valorFormatado
    return valorFormatado
  }).typeError(message)
})

// Validação usando Yup
const schema = computed(() => yup.object({
  // descricao: yup.string().required('Descrição é obrigatória'),
  conta_id: yup.string().required('Categoria é obrigatória'),
  valor: yup.number().formatted('Valor deve ser um número válido').required('Valor é obrigatório').min(0, 'Valor não pode ser negativo'),
  vencimento: yup.date()
    .typeError('Data de vencimento inválida')
    .required('Data de vencimento é obrigatória')
    .test('data-minima', 'Data não permitida devido à trava de competência', function (value) {
      if (!value || !dataMinima.value) { return true }
      const dataSelecionada = new Date(value)
      const dataMinimaNormalizada = new Date(dataMinima.value)
      return dataSelecionada >= dataMinimaNormalizada
    }),
  dataDoPagamento: yup.date()
    .typeError('Data de pagamento inválida')
    .nullable()
    .optional()
    .test('data-minima', 'Data não permitida devido à trava de competência', function (value) {
      if (!value || !dataMinima.value) { return true }
      const dataSelecionada = new Date(value)
      const dataMinimaNormalizada = new Date(dataMinima.value)
      return dataSelecionada >= dataMinimaNormalizada
    }),
  arquivos: yup.array().of(yup.object().nullable()),
}))

function cancelar () {
  store.resetarModalEditarDespesa()
}

const handleSubmit = async (values) => {
  try {
    useStore().loading = true

    // Create FormData object
    const formData = new FormData()

    // Prepare despesa data
    const despesaData = {
      id: modalEditarDespesa.value.id,
      conta_id: values.conta_id,
      descricao: values.descricao,
      valor: parseFloat(values.valor.toString().replace(',', '.')),
      vencimento: values.vencimento,
      data_pagamento: values.dataDoPagamento !== '' ? values.dataDoPagamento : null,
      observacao: values.observacao || null,
      criarLembrete: modalEditarDespesa.value.criarLembrete,
      calendarioId: modalEditarDespesa.value.calendarioId,
      despesa_efetiva: !modalEditarDespesa.value.despesaNaoEfetiva,
    }
    // Add despesa data as JSON
    formData.append('despesa', JSON.stringify(despesaData))

    // Add new files if they exist
    if (arquivos.value && arquivos.value.length > 0) {
      arquivos.value.forEach((arquivo) => {
        formData.append('arquivos[]', arquivo)
      })
    }

    // Verificar se há despesas duplicadas ao editar
    const despesaParaVerificar = [{
      ...despesaData,
      arquivos: arquivos.value,
    }]

    const duplicatasPorDespesa = await store.verificarTodasDespesasDuplicadas(despesaParaVerificar)

    if (duplicatasPorDespesa.length > 0) {
      // Se há duplicatas, usar substituirModal para alternar
      store.modalConfirmarDespesaDuplicada = {
        exibir: false,
        despesasParaSalvar: despesaParaVerificar,
        duplicatasEncontradas: duplicatasPorDespesa,
        callback: async () => {
          // Esta função será chamada se o usuário confirmar
          await editarDespesaConfirmado(despesaData.id, formData)
        },
      }
      useStore().substituirModal(store.modalConfirmarDespesaDuplicada, modalEditarDespesa.value)
      return // Interrompe o fluxo, aguarda decisão do usuário
    }

    // Se não há duplicatas, prossegue normalmente
    await editarDespesaConfirmado(despesaData.id, formData)
  } catch (err) {
    console.error('Erro ao atualizar despesa:', err)
    useStore().toasts.push({
      content: err.message || 'Erro ao atualizar despesa',
      color: 'danger',
    })
  } finally {
    useStore().loading = false
  }
}

// Função separada para editar despesa (usada tanto no fluxo normal quanto após confirmação)
const editarDespesaConfirmado = async (id: string, formData: FormData) => {
  await store.editarDespesa(id, formData)
  store.resetarModalEditarDespesa()
  arquivos.value = []
  useStore().toasts.push({ content: 'Despesa atualizada com sucesso', color: 'success' })
}

function contasAgrupadasPorCategoria () {
  const categorias = store.contas.reduce((acc: string[], conta: { categoria: string }) => {
    if (!acc.includes(conta.categoria)) {
      acc.push(conta.categoria)
    }
    return acc
  }, [])

  const contasAgrupadas = categorias.map((categoria) => {
    return {
      label: categoria,
      options: store.contas.filter(conta => conta.categoria === categoria).map(conta => ({ value: conta.id, label: conta.nome })),
    }
  })

  return contasAgrupadas
}

function atualizarArquivos (files) {
  // Filter out existing files and only get new ones
  arquivos.value = files
    .filter(file => file.file instanceof File)
    .map(file => file.file)
}

const excluirComprovante = async (comprovanteId) => {
  try {
    await useApiStore().request(
      `/financeiro/despesas/${modalEditarDespesa.value.id}/comprovantes/${comprovanteId}`,
      { method: 'DELETE' }
    )
    store.despesas.find(despesa => despesa.id === modalEditarDespesa.value.id).comprovantes = store.despesas.find(despesa => despesa.id === modalEditarDespesa.value.id).comprovantes.filter(comprovante => comprovante.id !== comprovanteId)

    useStore().toasts.push({ content: 'Comprovante excluído com sucesso', color: 'success' })
  } catch (error) {
    console.error('Erro ao excluir comprovante:', error)
    useStore().toasts.push({ content: 'Erro ao excluir comprovante', color: 'danger' })
  }
}

const excluirEventoCalendario = async () => {
  try {
    if (modalEditarDespesa.value.eventoCalendario) {
      await store.deletarEventoCalendario(modalEditarDespesa.value.eventoCalendario.id)
      modalEditarDespesa.value.eventoCalendario = null
      modalEditarDespesa.value.criarLembrete = false
    }
  } catch (error) {
    console.error('Erro ao excluir evento de calendário:', error)
  }
}

// Adicione este computed property
const larkTooltip = computed(() => {
  if (!larkStore.isAuthenticated) {
    return 'Conecte-se ao Lark para criar lembretes'
  }
  if (!larkStore.calendarId) {
    return 'Selecione um calendário padrão nas configurações do Lark'
  }
  return 'Marque se deseja ser lembrado no calendário do Lark'
})

const canCreateReminder = computed(() => {
  return larkStore.isAuthenticated && !!larkStore.calendarId
})

// Funções para gerenciar despesas recorrentes
async function buscarDespesasRecorrentes () {
  const despesa = store.despesas.find(d => d.id === modalEditarDespesa.value.id)
  if (despesa?.recorrencia_id) {
    try {
      carregandoRecorrencias.value = true
      despesasRecorrentes.value = await store.buscarDespesasRecorrentes(despesa.recorrencia_id)
      // Adicionar propriedades para edição
      despesasRecorrentes.value = despesasRecorrentes.value.map(d => ({
        ...d,
        editando: false,
        novoValor: d.valor,
        novoVencimento: d.vencimento,
        excluindo: false,
        mostrarComprovantes: false,
        gerenciandoAnexos: false,
      }))
    } catch (error) {
      console.error('Erro ao buscar despesas recorrentes:', error)
      useStore().toasts.push({
        content: 'Erro ao buscar despesas recorrentes',
        color: 'danger',
      })
    } finally {
      carregandoRecorrencias.value = false
    }
  }
}

// Função para converter valor em reais para centavos (para a máscara)
function converterParaCentavos (valor) {
  if (typeof valor === 'number') {
    return Math.round(valor * 100)
  }
  if (typeof valor === 'string') {
    // Se já está em formato de string com vírgula, converter para número primeiro
    const numeroValor = parseFloat(valor.replace(/\./g, '').replace(',', '.'))
    return Math.round(numeroValor * 100)
  }
  return 0
}

// Adicionando uma função para preservar o valor original ao alternar abas
function preservarValoresOriginais () {
  // Guardar valor original convertido para centavos para a máscara
  const valorOriginalEmCentavos = converterParaCentavos(modalEditarDespesa.value.valor)

  // Certificar que o valor é mantido no formato original
  watch(() => abaAtiva.value, () => {
    // Restaurar o valor original depois de um breve delay para evitar conflitos com outras renderizações
    setTimeout(() => {
      modalEditarDespesa.value.valor = valorOriginalEmCentavos
    }, 50)
  })
}

// Verifica se tem recorrência quando o modal é exibido
watch(() => modalEditarDespesa.value.exibir, (novoValor) => {
  if (novoValor) {
    console.log('Modal exibido, descrição:', modalEditarDespesa.value.descricao)

    // Converter valor para centavos para a máscara funcionar corretamente
    if (typeof modalEditarDespesa.value.valor === 'number') {
      modalEditarDespesa.value.valor = converterParaCentavos(modalEditarDespesa.value.valor)
    }

    buscarDespesasRecorrentes()
    preservarValoresOriginais()
  } else {
    despesasRecorrentes.value = []
    despesasSelecionadasParaRemocao.value = []
  }
})

// Watch para debugging da descrição
watch(() => modalEditarDespesa.value.descricao, (novoValor) => {
  console.log('Descrição alterada:', novoValor)
}, { immediate: true })

function iniciarEdicaoDespesaRecorrente (despesa) {
  // Resetar edição para outras despesas
  despesasRecorrentes.value.forEach((d) => {
    if (d.id !== despesa.id) { d.editando = false }
  })
  despesa.editando = true
}

function cancelarEdicaoDespesaRecorrente (despesa) {
  despesa.editando = false
  despesa.novoValor = despesa.valor
  despesa.novoVencimento = despesa.vencimento
}

async function salvarEdicaoDespesaRecorrente (despesa) {
  try {
    useStore().loading = true

    const formData = new FormData()
    const despesaData = {
      id: despesa.id,
      conta_id: despesa.conta_id,
      descricao: despesa.descricao,
      valor: typeof despesa.novoValor === 'string'
        ? parseFloat(despesa.novoValor.toString().replace(',', '.'))
        : despesa.novoValor,
      vencimento: despesa.novoVencimento,
      data_pagamento: despesa.data_pagamento,
      observacao: despesa.observacao || null,
      despesa_efetiva: despesa.despesa_efetiva,
    }

    formData.append('despesa', JSON.stringify(despesaData))

    await store.editarDespesa(despesaData.id, formData)

    // Atualizar os valores na lista
    despesa.valor = despesaData.valor
    despesa.vencimento = despesaData.vencimento
    despesa.editando = false

    useStore().toasts.push({
      content: 'Despesa recorrente atualizada com sucesso',
      color: 'success',
    })
  } catch (err) {
    console.error('Erro ao atualizar despesa recorrente:', err)
    useStore().toasts.push({
      content: err.message || 'Erro ao atualizar despesa recorrente',
      color: 'danger',
    })
  } finally {
    useStore().loading = false
  }
}

function toggleSelecionarDespesaParaRemocao (despesaId) {
  const index = despesasSelecionadasParaRemocao.value.indexOf(despesaId)
  if (index === -1) {
    despesasSelecionadasParaRemocao.value.push(despesaId)
  } else {
    despesasSelecionadasParaRemocao.value.splice(index, 1)
  }
}

async function removerDespesSelecionadas () {
  if (despesasSelecionadasParaRemocao.value.length === 0) { return }

  try {
    useStore().loading = true

    // Log para depuração
    console.log('IDs das despesas selecionadas para remoção:', despesasSelecionadasParaRemocao.value)

    const promises = despesasSelecionadasParaRemocao.value.map((id) => {
      // Verificar se o ID existe no array de despesasRecorrentes
      const despesa = despesasRecorrentes.value.find(d => d.id === id)
      if (!despesa) {
        console.error(`Despesa com ID ${id} não encontrada no array de despesasRecorrentes`)
        return Promise.reject(new Error(`Despesa com ID ${id} não encontrada`))
      }

      // Passar o ID correto para o método de excluir despesa
      return store.excluirDespesa(id, false)
    })

    await Promise.all(promises)
    await store.fetchDespesas({ competencia: useStore().competenciaFormatada })
    // Remover da lista local
    despesasRecorrentes.value = despesasRecorrentes.value.filter(
      d => !despesasSelecionadasParaRemocao.value.includes(d.id)
    )

    despesasSelecionadasParaRemocao.value = []

    useStore().toasts.push({
      content: 'Despesas removidas com sucesso',
      color: 'success',
    })

    // Se todas foram removidas, recarregar as despesas
    if (despesasRecorrentes.value.length === 0) {
      await store.fetchDespesas({ competencia: useStore().competenciaFormatada })
    }
  } catch (error) {
    console.error('Erro ao remover despesas:', error)
    useStore().toasts.push({
      content: 'Erro ao remover despesas: ' + (error.message || 'Erro desconhecido'),
      color: 'danger',
    })
  } finally {
    useStore().loading = false
  }
}

function configurarRecorrencia () {
  const despesaAtual = store.despesas.find(d => d.id === modalEditarDespesa.value.id)
  if (!despesaAtual) { return }

  // Verificar se a despesa já possui recorrência
  if (despesaAtual.recorrencia_id) {
    // Se já tem recorrência, apenas permitir adicionar novas parcelas
    configurarAdicionarParcelasRecorrencia(despesaAtual)
  } else {
    // Se não tem recorrência, criar uma nova
    criarNovaRecorrencia()
  }
}

function criarNovaRecorrencia () {
  // Função auxiliar para conversão segura de valor
  const converterValor = (valor) => {
    // Se já é um número, retorna diretamente
    if (typeof valor === 'number') {
      return valor
    }

    // Se é string, remove formatação brasileira
    if (typeof valor === 'string') {
      // Remove pontos (separadores de milhares) e converte vírgula para ponto
      return parseFloat(valor.replace(/\./g, '').replace(',', '.'))
    }

    return 0
  }

  // Preparar o objeto para o modal de configuração de recorrência
  store.modalConfigurarRecorrencia.despesaBase = {
    ...modalEditarDespesa.value,
    valor: converterValor(modalEditarDespesa.value.valor),
  }

  adicionandoNovasRecorrencias.value = true
  useStore().substituirModal(store.modalConfigurarRecorrencia, modalEditarDespesa.value)
}

async function configurarAdicionarParcelasRecorrencia (despesaAtual) {
  try {
    // Buscar todas as despesas da mesma recorrência
    const despesasRecorrentes = await store.buscarDespesasRecorrentes(despesaAtual.recorrencia_id)

    // Preparar modal com dados das despesas existentes (somente leitura)
    store.modalConfigurarRecorrencia.despesaBase = {
      ...modalEditarDespesa.value,
      valor: typeof modalEditarDespesa.value.valor === 'string'
        ? parseFloat(modalEditarDespesa.value.valor.replace(/\./g, '').replace(',', '.'))
        : modalEditarDespesa.value.valor,
    }

    // Configurar datas existentes (somente leitura)
    store.modalConfigurarRecorrencia.datas = despesasRecorrentes
      .sort((a, b) => new Date(a.vencimento).getTime() - new Date(b.vencimento).getTime())
      .map(d => d.vencimento)

    // Marcar como modo de adição de parcelas existentes
    store.modalConfigurarRecorrencia.modoEdicaoRecorrencia = true
    store.modalConfigurarRecorrencia.despesasExistentes = despesasRecorrentes

    adicionandoNovasRecorrencias.value = true
    useStore().substituirModal(store.modalConfigurarRecorrencia, modalEditarDespesa.value)
  } catch (error) {
    console.error('Erro ao carregar despesas recorrentes:', error)
    useStore().toasts.push({
      content: 'Erro ao carregar despesas recorrentes',
      color: 'danger',
    })
  }
}

// Watch para quando o modalConfigurarRecorrencia for fechado e retornar para a edição
watch(() => modalConfigurarRecorrencia.value.exibir, async (exibindo) => {
  if (!exibindo && adicionandoNovasRecorrencias.value) {
    adicionandoNovasRecorrencias.value = false

    // Verifica se o usuário confirmou a criação de recorrências
    if (modalConfigurarRecorrencia.value.confirmado && modalConfigurarRecorrencia.value.datas.length > 0) {
      await criarNovasDespesasRecorrentes()
    } else {
      // Se o usuário cancelou, precisamos garantir que o modal de edição seja reaberto
      // Procura na lista de modais abertos o modal de edição
      const modalAnterior = useStore().modaisAbertos.find(m => m.modal === modalConfigurarRecorrencia.value)
      if (modalAnterior && modalAnterior.aoFechar) {
        // Remove o modal de configuração da lista de modais abertos
        useStore().modaisAbertos = useStore().modaisAbertos.filter(
          m => m.modal !== modalConfigurarRecorrencia.value
        )

        // Exibe o modal de edição novamente
        modalAnterior.aoFechar.exibir = true
      } else {
        // Fallback: reabrir o modal de edição diretamente
        modalEditarDespesa.value.exibir = true
      }
    }
  }
})

async function criarNovasDespesasRecorrentes () {
  try {
    useStore().loading = true

    // Verificar se a despesa original já tem um recorrencia_id para reutilizar
    const despesaOriginal = store.despesas.find(d => d.id === modalEditarDespesa.value.id)

    // Se a despesa já tem uma recorrência, usamos o mesmo ID, caso contrário criamos um novo
    const recorrenciaId = despesaOriginal?.recorrencia_id || uuidv4()

    // Verificar se estamos em modo de edição de recorrência existente
    const modoEdicaoRecorrencia = modalConfigurarRecorrencia.value.modoEdicaoRecorrencia
    const despesasExistentes = modalConfigurarRecorrencia.value.despesasExistentes || []

    let despesasFuturas = []

    if (modoEdicaoRecorrencia && despesasExistentes.length > 0) {
      // MODO EDIÇÃO: Adicionar novas parcelas à recorrência existente
      const totalParcelasFinais = despesasExistentes.length + modalConfigurarRecorrencia.value.quantidade

      // As datas já são apenas as novas parcelas, não fazer slice
      despesasFuturas = modalConfigurarRecorrencia.value.datas.map((data, index) => ({
        conta_id: modalEditarDespesa.value.conta_id,
        descricao: modalEditarDespesa.value.descricao,
        valor: typeof modalEditarDespesa.value.valor === 'string'
          ? parseFloat(modalEditarDespesa.value.valor.toString().replace('.', '').replace(',', '.'))
          : modalEditarDespesa.value.valor,
        vencimento: data,
        recorrencia_id: recorrenciaId,
        numero_parcela: despesasExistentes.length + index + 1, // Continuar numeração
        total_parcelas: totalParcelasFinais,
        observacao: modalEditarDespesa.value.observacao || null,
        despesa_efetiva: !modalEditarDespesa.value.despesaNaoEfetiva,
      }))
    } else {
      // MODO CRIAÇÃO: Criar nova recorrência do zero
      despesasFuturas = modalConfigurarRecorrencia.value.datas
        .slice(1) // Pula a primeira data (que é a original)
        .map((data, index) => ({
          conta_id: modalEditarDespesa.value.conta_id,
          descricao: modalEditarDespesa.value.descricao,
          valor: typeof modalEditarDespesa.value.valor === 'string'
            ? parseFloat(modalEditarDespesa.value.valor.toString().replace('.', '').replace(',', '.'))
            : modalEditarDespesa.value.valor,
          vencimento: data,
          recorrencia_id: recorrenciaId,
          numero_parcela: index + 2, // Começa em 2 porque pulamos a primeira
          total_parcelas: modalConfigurarRecorrencia.value.datas.length,
          observacao: modalEditarDespesa.value.observacao || null,
          despesa_efetiva: !modalEditarDespesa.value.despesaNaoEfetiva,
        }))
    }

    // Verificar se há duplicatas nas novas despesas recorrentes
    if (despesasFuturas.length > 0) {
      const duplicatasPorDespesa = await store.verificarTodasDespesasDuplicadas(despesasFuturas)

      if (duplicatasPorDespesa.length > 0) {
        // Se há duplicatas, usar substituirModal para alternar
        store.modalConfirmarDespesaDuplicada = {
          exibir: false,
          despesasParaSalvar: despesasFuturas,
          duplicatasEncontradas: duplicatasPorDespesa,
          callback: async () => {
            // Esta função será chamada se o usuário confirmar
            await criarDespesasRecorrentesConfirmado(despesaOriginal, recorrenciaId, despesasFuturas)
          },
        }
        useStore().substituirModal(store.modalConfirmarDespesaDuplicada, modalEditarDespesa.value)
        return // Interrompe o fluxo, aguarda decisão do usuário
      }
    }

    // Se não há duplicatas, prossegue normalmente
    await criarDespesasRecorrentesConfirmado(despesaOriginal, recorrenciaId, despesasFuturas)
  } catch (error) {
    console.error('Erro ao criar despesas recorrentes:', error)
    useStore().toasts.push({
      content: 'Erro ao criar despesas recorrentes: ' + (error.message || 'Erro desconhecido'),
      color: 'danger',
    })
  } finally {
    useStore().loading = false
    // Resetar o estado
    store.resetarModalConfigurarRecorrencia()
  }
}

// Função separada para criar as despesas recorrentes (usada tanto no fluxo normal quanto após confirmação)
const criarDespesasRecorrentesConfirmado = async (despesaOriginal: any, recorrenciaId: string, despesasFuturas: any[]) => {
  // Garantir que o valor seja processado corretamente independente do formato
  const tratarValor = (valor) => {
    if (typeof valor === 'number') { return valor }

    // Remover possível símbolo de R$ ou espaços
    let valorString = valor.toString().replace('R$', '').trim()

    // Se estiver no formato brasileiro (ex: 1.500,00)
    if (valorString.includes(',')) {
      // Remover todos os pontos (separadores de milhar)
      valorString = valorString.replace(/\./g, '')
      // Substituir a vírgula por ponto
      valorString = valorString.replace(',', '.')
    }

    const resultado = parseFloat(valorString)
    console.log('Valor original:', valor, 'Valor convertido:', resultado)
    return resultado
  }

  // Verificar se estamos em modo de edição de recorrência existente
  const modoEdicaoRecorrencia = modalConfigurarRecorrencia.value.modoEdicaoRecorrencia
  const despesasExistentes = modalConfigurarRecorrencia.value.despesasExistentes || []

  if (modoEdicaoRecorrencia && despesasExistentes.length > 0) {
    // MODO EDIÇÃO: Atualizar apenas o total_parcelas das existentes
    const totalParcelasFinais = despesasExistentes.length + modalConfigurarRecorrencia.value.quantidade

    // Atualizar todas as despesas existentes com o novo total
    for (const despesaExistente of despesasExistentes) {
      const formData = new FormData()
      const despesaData = {
        id: despesaExistente.id,
        conta_id: despesaExistente.conta_id,
        descricao: despesaExistente.descricao,
        valor: despesaExistente.valor,
        vencimento: despesaExistente.vencimento,
        data_pagamento: despesaExistente.data_pagamento,
        observacao: despesaExistente.observacao,
        despesa_efetiva: despesaExistente.despesa_efetiva,
        recorrencia_id: despesaExistente.recorrencia_id,
        numero_parcela: despesaExistente.numero_parcela, // Manter número original
        total_parcelas: totalParcelasFinais, // Atualizar apenas o total
      }
      formData.append('despesa', JSON.stringify(despesaData))

      await store.editarDespesa(despesaExistente.id, formData)
    }
  } else {
    // MODO CRIAÇÃO: Código existente para criar nova recorrência
    const totalParcelas = modalConfigurarRecorrencia.value.datas.length

    // 1. PRIMEIRO: Atualizar a despesa original (sempre será parcela 1)
    const formData = new FormData()
    const despesaData = {
      id: despesaOriginal.id,
      conta_id: modalEditarDespesa.value.conta_id,
      descricao: modalEditarDespesa.value.descricao,
      valor: tratarValor(modalEditarDespesa.value.valor),
      vencimento: despesaOriginal.vencimento,
      data_pagamento: despesaOriginal.data_pagamento,
      observacao: modalEditarDespesa.value.observacao || null,
      despesa_efetiva: !modalEditarDespesa.value.despesaNaoEfetiva,
      recorrencia_id: recorrenciaId,
      numero_parcela: 1,
      total_parcelas: totalParcelas,
    }
    formData.append('despesa', JSON.stringify(despesaData))

    // Anexar arquivos: originais + compilados para a primeira parcela
    const arquivosCompilados = modalConfigurarRecorrencia.value.arquivosIndividuais || {}
    const arquivosOriginais = arquivos.value || []
    const arquivosPrimeiraParcela = arquivosCompilados[0] || []
    const todosArquivosPrimeiraParcela = [...arquivosOriginais, ...arquivosPrimeiraParcela]

    if (todosArquivosPrimeiraParcela.length > 0) {
      todosArquivosPrimeiraParcela.forEach((arquivo) => {
        formData.append('arquivos[]', arquivo)
      })
    }

    await store.editarDespesa(despesaOriginal.id, formData)
  }

  // 2. SEGUNDO: Criar despesas para as datas restantes (já foram verificadas no início da função)

  // Salvar apenas as despesas futuras (se houver) COM SEUS RESPECTIVOS ANEXOS
  if (despesasFuturas.length > 0) {
    const arquivosCompilados = modalConfigurarRecorrencia.value.arquivosIndividuais || {}

    // Para cada despesa futura, anexar os arquivos correspondentes
    for (let i = 0; i < despesasFuturas.length; i++) {
      // Em modo de edição, os índices já correspondem às novas parcelas
      const indiceParcela = modoEdicaoRecorrencia ? i : i + 1
      const arquivosDaParcela = arquivosCompilados[indiceParcela] || []

      if (arquivosDaParcela.length > 0) {
        // Adicionar os arquivos à despesa
        despesasFuturas[i].arquivos = arquivosDaParcela
        console.log(`Anexando ${arquivosDaParcela.length} arquivo(s) à parcela ${indiceParcela + 1}`)
      }
    }

    await store.salvarDespesa(despesasFuturas)
  }

  // Recarregar as despesas para mostrar as novas
  await store.fetchDespesas({ competencia: useStore().competenciaFormatada })

  // Informação para o usuário
  const totalDespesasProcessadas = despesasFuturas.length + 1 // +1 para a original atualizada
  useStore().toasts.push({
    content: `Recorrência criada com sucesso! ${totalDespesasProcessadas} despesas processadas`,
    color: 'success',
  })

  // Fechar o modal de edição após salvar
  store.resetarModalEditarDespesa()
  arquivos.value = []
}

// Funções para gerenciar arquivos nas despesas recorrentes
function abrirGerenciadorDeAnexos (despesa) {
  // Limpa todos os anexos em foco
  despesaRecorrenteEmFoco.value = despesa
  if (!arquivosRecorrencia.value[despesa.id]) {
    arquivosRecorrencia.value[despesa.id] = []
  }

  // Expande a linha para mostrar a interface de gerenciamento de arquivos
  despesa.gerenciandoAnexos = true
}

function fecharGerenciadorDeAnexos (despesa) {
  despesa.gerenciandoAnexos = false
  despesaRecorrenteEmFoco.value = null
}

function atualizarArquivosRecorrencia (id, files) {
  // Filter out existing files and only get new ones
  arquivosRecorrencia.value[id] = files
    .filter(file => file.file instanceof File)
    .map(file => file.file)
}

const excluirComprovanteRecorrencia = async (despesaId, comprovanteId) => {
  try {
    useStore().loading = true
    await useApiStore().request(
      `/financeiro/despesas/${despesaId}/comprovantes/${comprovanteId}`,
      { method: 'DELETE' }
    )

    // Atualize a lista de recorrências para refletir a alteração
    const despesaIndex = despesasRecorrentes.value.findIndex(d => d.id === despesaId)
    if (despesaIndex !== -1) {
      const comprovantes = despesasRecorrentes.value[despesaIndex].comprovantes
      despesasRecorrentes.value[despesaIndex].comprovantes = comprovantes.filter(c => c.id !== comprovanteId)
    }

    useStore().toasts.push({ content: 'Comprovante excluído com sucesso', color: 'success' })
  } catch (error) {
    console.error('Erro ao excluir comprovante:', error)
    useStore().toasts.push({ content: 'Erro ao excluir comprovante', color: 'danger' })
  } finally {
    useStore().loading = false
  }
}

async function salvarArquivosRecorrencia (despesa) {
  try {
    if (!arquivosRecorrencia.value[despesa.id] || arquivosRecorrencia.value[despesa.id].length === 0) {
      despesa.gerenciandoAnexos = false
      return
    }

    useStore().loading = true

    const formData = new FormData()

    // Dados da despesa sem alterar nada
    const despesaData = {
      id: despesa.id,
      conta_id: despesa.conta_id,
      descricao: despesa.descricao,
      valor: despesa.valor,
      vencimento: despesa.vencimento,
      data_pagamento: despesa.data_pagamento,
      observacao: despesa.observacao || null,
      despesa_efetiva: despesa.despesa_efetiva,
    }

    formData.append('despesa', JSON.stringify(despesaData))

    // Anexar arquivos
    arquivosRecorrencia.value[despesa.id].forEach((arquivo) => {
      formData.append('arquivos[]', arquivo)
    })

    await store.editarDespesa(despesa.id, formData)

    // Atualiza a lista de comprovantes para incluir os novos arquivos
    const despesaAtualizada = await store.buscarDespesasRecorrentes(despesa.recorrencia_id)
    const despesaIndex = despesasRecorrentes.value.findIndex(d => d.id === despesa.id)

    if (despesaIndex !== -1 && despesaAtualizada) {
      const despesaAtualizadaItem = despesaAtualizada.find(d => d.id === despesa.id)
      if (despesaAtualizadaItem) {
        despesasRecorrentes.value[despesaIndex].comprovantes = despesaAtualizadaItem.comprovantes
      }
    }

    // Limpa os arquivos depois de salvar
    arquivosRecorrencia.value[despesa.id] = []
    despesa.gerenciandoAnexos = false
    despesaRecorrenteEmFoco.value = null

    useStore().toasts.push({
      content: 'Comprovantes adicionados com sucesso',
      color: 'success',
    })
  } catch (error) {
    console.error('Erro ao salvar comprovantes:', error)
    useStore().toasts.push({
      content: 'Erro ao salvar comprovantes: ' + (error.message || 'Erro desconhecido'),
      color: 'danger',
    })
  } finally {
    useStore().loading = false
  }
}

// Funções para gerenciar arquivos em massa para todas as recorrências
const arquivosMassaRecorrencia = ref([])
const selecionadasParaAnexos = ref([])
const mostraSeletorAnexosMassa = ref(false)

function abrirUploadEmMassa () {
  // Inicializa a lista de despesas selecionadas com todas as despesas recorrentes
  // exceto a atual (sendo editada) e as que já estão pagas
  selecionadasParaAnexos.value = despesasRecorrentes.value
    .filter(d => !d.data_pagamento && d.id !== modalEditarDespesa.value.id)
    .map(d => d.id)

  // Limpa os arquivos
  arquivosMassaRecorrencia.value = []

  // Exibe o seletor
  mostraSeletorAnexosMassa.value = true
}

function fecharUploadEmMassa () {
  mostraSeletorAnexosMassa.value = false
  arquivosMassaRecorrencia.value = []
  selecionadasParaAnexos.value = []
}

function atualizarArquivosMassaRecorrencia (files) {
  // Filter out existing files and only get new ones
  arquivosMassaRecorrencia.value = files
    .filter(file => file.file instanceof File)
    .map(file => file.file)
}

function toggleSelecionarDespesaParaAnexos (despesaId) {
  const index = selecionadasParaAnexos.value.indexOf(despesaId)
  if (index === -1) {
    selecionadasParaAnexos.value.push(despesaId)
  } else {
    selecionadasParaAnexos.value.splice(index, 1)
  }
}

async function salvarAnexosEmMassa () {
  if (arquivosMassaRecorrencia.value.length === 0) {
    useStore().toasts.push({
      content: 'Nenhum arquivo selecionado',
      color: 'warning',
    })
    return
  }

  if (selecionadasParaAnexos.value.length === 0) {
    useStore().toasts.push({
      content: 'Nenhuma despesa selecionada',
      color: 'warning',
    })
    return
  }

  try {
    useStore().loading = true

    // Para cada despesa selecionada, fazer o upload dos mesmos arquivos
    const promises = selecionadasParaAnexos.value.map(async (despesaId) => {
      const despesa = despesasRecorrentes.value.find(d => d.id === despesaId)
      if (!despesa) { return null }

      const formData = new FormData()

      // Preparar os dados da despesa sem alterá-los
      const despesaData = {
        id: despesa.id,
        conta_id: despesa.conta_id,
        descricao: despesa.descricao,
        valor: despesa.valor,
        vencimento: despesa.vencimento,
        data_pagamento: despesa.data_pagamento,
        observacao: despesa.observacao || null,
        despesa_efetiva: despesa.despesa_efetiva,
      }

      formData.append('despesa', JSON.stringify(despesaData))

      // Anexar cada arquivo
      arquivosMassaRecorrencia.value.forEach((arquivo) => {
        formData.append('arquivos[]', arquivo)
      })

      // Enviar para o backend
      return await store.editarDespesa(despesa.id, formData)
    })

    await Promise.all(promises)

    // Recarregar as despesas recorrentes para mostrar os novos anexos
    await buscarDespesasRecorrentes()

    useStore().toasts.push({
      content: `Anexos adicionados a ${selecionadasParaAnexos.value.length} despesas com sucesso`,
      color: 'success',
    })

    // Fechar a interface
    fecharUploadEmMassa()
  } catch (error) {
    console.error('Erro ao adicionar anexos em massa:', error)
    useStore().toasts.push({
      content: 'Erro ao adicionar anexos: ' + (error.message || 'Erro desconhecido'),
      color: 'danger',
    })
  } finally {
    useStore().loading = false
  }
}

const contaDespesaEfetiva = computed(() => {
  const conta = store.contas.find((c: any) => c.id === modalEditarDespesa.value.conta_id)
  return conta?.despesa_efetiva === true
})
</script>

<template>
  <div>
    <CModal
      id="modalEditarDespesa"
      :visible="modalEditarDespesa.exibir"
      size="xl"
      backdrop="static"
      @close="cancelar"
    >
      <CModalHeader>
        <CModalTitle id="editarDespesa">
          Editar Despesa
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <!-- Abas para navegação -->
        <CTabs v-model="abaAtiva" class="mb-3">
          <CNav variant="tabs">
            <CNavItem>
              <CNavLink href="#" :active="abaAtiva === 'detalhes'" @click="abaAtiva = 'detalhes'">
                Detalhes
              </CNavLink>
            </CNavItem>
            <CNavItem v-if="store.despesas.find(d => d.id === modalEditarDespesa.id)?.recorrencia_id">
              <CNavLink href="#" :active="abaAtiva === 'recorrencia'" @click="abaAtiva = 'recorrencia'">
                Recorrência
              </CNavLink>
            </CNavItem>
          </CNav>
        </CTabs>

        <!-- Aba de Detalhes -->
        <div v-if="abaAtiva === 'detalhes'">
          <Form :validation-schema="schema" :initial-values="modalEditarDespesa" @submit="handleSubmit">
            <CRow class="mb-3">
              <CCol :md="12">
                <div class="mb-2">
                  <label class="form-label" for="conta">
                    Categoria
                  </label>
                  <Field v-slot="{ field, meta }" v-model="modalEditarDespesa.conta_id" name="conta_id">
                    <Multiselect
                      v-bind="field"
                      :options="contasAgrupadasPorCategoria()"
                      :searchable="true"
                      :create-option="false"
                      placeholder="Selecione ou pesquise"
                      :class="useStore().retornarClasseDeValidacao(meta)"
                      :groups="true"
                      @change="() => {
                        if (meta.touched && meta.valid) {
                          modalEditarDespesa.despesaNaoEfetiva = !contaDespesaEfetiva
                        }
                      }"
                    />
                  </Field>
                  <ErrorMessage name="conta_id" class="invalid-feedback" />
                </div>
              </CCol>

              <CCol :md="12">
                <div v-if="contaDespesaEfetiva" class="mb-2">
                  <label v-c-tooltip="'Considera-se despesa indireta aquela lançada exclusivamente para fins de Livro Caixa da Receita Federal, necessária à percepção da receita e à manutenção da fonte produtora.'" class="form-label" for="checkboxDespesaEfetiva">
                    <input id="checkboxDespesaEfetiva" v-model="modalEditarDespesa.despesaNaoEfetiva" type="checkbox" class="form-check-input">
                    Não é despesa direta
                  </label>
                </div>
                <div v-else-if="modalEditarDespesa.conta_id" class="alert alert-info d-flex align-items-center gap-2 p-2 rounded">
                  <font-awesome-icon icon="fa-solid fa-info" class="ms-1" />
                  <span class="">
                    Esta despesa será lançada como despesa INDIRETA
                  </span>
                </div>
              </CCol>

              <CCol :md="12">
                <div class="mb-2">
                  <label class="form-label" for="categoria">
                    Descrição
                  </label>
                  <Field v-slot="{ field, meta }" v-model="modalEditarDespesa.descricao" name="descricao">
                    <RichTextEditor
                      :model-value="modalEditarDespesa.descricao"
                      :class="useStore().retornarClasseDeValidacao(meta)"
                      @update:model-value="val => { console.log('Description updated:', val); modalEditarDespesa.descricao = val; field.onChange(val); }"
                    />
                  </Field>
                  <ErrorMessage name="descricao" class="invalid-feedback" />
                </div>
              </CCol>

              <CCol :md="6">
                <div class="mb-2">
                  <label class="form-label" for="valor">
                    Valor
                  </label>
                  <Field v-slot="{ field, meta }" name="valor">
                    <input
                      id="valor"
                      v-bind="field"
                      v-maska
                      type="text"
                      label="Valor"
                      data-maska="##########,##"
                      data-maska-reversed
                      class="form-control"
                      :class="useStore().retornarClasseDeValidacao(meta)"
                      required
                    >
                  </Field>
                  <ErrorMessage name="valor" class="invalid-feedback" />
                </div>
              </CCol>

              <CCol :md="6">
                <div class="mb-2">
                  <label class="form-label" for="dataParaEfetuarPagamento">
                    Vencimento
                  </label>
                  <Field v-slot="{ field, meta }" name="vencimento">
                    <input v-bind="field" type="date" class="form-control" :class="useStore().retornarClasseDeValidacao(meta)" :min="dataMinima">
                  </Field>
                  <ErrorMessage name="vencimento" class="invalid-feedback" />
                </div>
              </CCol>

              <CCol :md="6">
                <div class="mb-2">
                  <label class="form-label" for="dataDoPagamento">
                    Data do Pagamento
                  </label>
                  <Field v-slot="{ field, meta }" name="dataDoPagamento">
                    <input
                      v-bind="field"
                      type="date"
                      class="form-control"
                      :class="useStore().retornarClasseDeValidacao(meta)"
                      :max="dataAtual"
                    >
                  </Field>
                  <ErrorMessage name="dataDoPagamento" class="invalid-feedback" />
                </div>
              </CCol>
            </CRow>

            <div v-if="existingFiles?.length" class="mb-3">
              <h6>Fatura / Comprovantes:</h6>
              <div class="list-group">
                <template
                  v-for="comprovante in existingFiles"
                  :key="comprovante.id"
                >
                  <div
                    v-if="comprovante.arquivo_tipo !== 'ARQUIVOS_MESCLADOS'"
                    class="list-group-item d-flex justify-content-between align-items-center comprovante-item"
                  >
                    <span
                      class="text-primary comprovante-link"
                      @click="store.downloadComprovante(modalEditarDespesa.id, comprovante.id)"
                    >
                      <CIcon name="cil-cloud-download" class="me-2" />
                      {{ comprovante.arquivo_tipo === 'COMPROVANTE_UNIFICADO' ? 'Comprovante Unificado' : comprovante.arquivo_nome }}
                    </span>
                    <button class="btn btn-danger btn-sm" @click.prevent="excluirComprovante(comprovante.id)">
                      <CIcon icon="cil-trash" />
                    </button>
                  </div>
                </template>
              </div>
            </div>

            <div class="mb-3">
              <h6>Adicionar novos comprovantes:</h6>
              <FilePond
                ref="pond"
                name="arquivos"
                :files="arquivos"
                :label-idle="'Arraste e solte seus arquivos ou <u>clique aqui</u>'"
                :allow-multiple="true"
                :accepted-file-types="['application/pdf', 'image/*']"
                @updatefiles="atualizarArquivos"
              />
            </div>

            <div class="my-3">
              <label class="form-label" for="observacao">
                Observação
              </label>
              <Field v-slot="{ field }" name="observacao">
                <RichTextEditor
                  :model-value="modalEditarDespesa.observacao"
                  @update:model-value="val => { console.log('Observacao:', val); modalEditarDespesa.observacao = val; field.onChange(val); }"
                />
              </Field>
            </div>

            <!-- Adicionar lembrete -->
            <div class="mb-3">
              <div
                v-if="modalEditarDespesa.eventoCalendario && modalEditarDespesa.eventoCalendario?.id"
                class="alert alert-info d-flex align-items-center gap-2 p-2 rounded"
              >
                <CIcon icon="cil-calendar-check" size="lg" class="text-primary" />
                <span>Esta despesa já possui um evento agendado no calendário.</span>
                <CButton color="danger" size="sm" @click="excluirEventoCalendario">
                  <CIcon icon="cil-trash" />
                  Excluir Agendamento
                </CButton>
              </div>
              <div
                v-else
                v-c-tooltip="larkTooltip"
                class="form-check d-flex align-items-center gap-2 p-2 rounded hover-highlight"
              >
                <input
                  id="criarLembrete"
                  v-model="modalEditarDespesa.criarLembrete"
                  type="checkbox"
                  class="cursor-pointer"
                  :disabled="!canCreateReminder"
                >
                <label
                  class="form-check-label d-flex align-items-center gap-2"
                  :class="{
                    'cursor-pointer': canCreateReminder,
                    'text-muted': !canCreateReminder
                  }"
                  for="criarLembrete"
                >
                  <CIcon
                    icon="cil-bell"
                    size="lg"
                    :class="[
                      modalEditarDespesa.criarLembrete && canCreateReminder ? 'text-primary' : 'text-secondary',
                      !canCreateReminder ? 'opacity-50' : ''
                    ]"
                  />
                  <span>Criar lembrete no dia do vencimento</span>
                </label>
              </div>
            </div>

            <CModalFooter>
              <CButton color="secondary" @click="cancelar">
                Cancelar
              </CButton>
              <CButton
                color="info"
                class="me-2"
                @click="configurarRecorrencia"
              >
                <CIcon icon="cil-loop-circular" class="me-1" />
                {{ store.despesas.find(d => d.id === modalEditarDespesa.id)?.recorrencia_id ? 'Gerenciar Recorrência' : 'Criar Recorrência' }}
              </CButton>
              <CButton type="submit" color="primary">
                Atualizar
              </CButton>
            </CModalFooter>
          </Form>
        </div>

        <!-- Aba de Recorrência -->
        <div v-else-if="abaAtiva === 'recorrencia'">
          <div v-if="carregandoRecorrencias" class="text-center py-4">
            <CSpinner />
            <p class="mt-2">
              Carregando despesas recorrentes...
            </p>
          </div>

          <div v-else-if="despesasRecorrentes.length > 0">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0">
                Despesas desta série de recorrência
              </h6>
              <div>
                <CButton
                  v-if="despesasSelecionadasParaRemocao.length > 0"
                  color="danger"
                  size="sm"
                  @click="removerDespesSelecionadas"
                >
                  <CIcon icon="cil-trash" class="me-1" />
                  Remover selecionadas ({{ despesasSelecionadasParaRemocao.length }})
                </CButton>
                <CButton
                  color="info"
                  size="sm"
                  @click="abrirUploadEmMassa"
                >
                  <CIcon icon="cil-cloud-upload" class="me-1" />
                  Upload em Massa
                </CButton>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table">
                <thead>
                  <tr>
                    <th style="width: 40px">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :checked="despesasSelecionadasParaRemocao.length === despesasRecorrentes.filter(d => !d.data_pagamento && d.id !== modalEditarDespesa.id).length && despesasSelecionadasParaRemocao.length > 0"
                        @change="despesasSelecionadasParaRemocao = $event.target.checked ? despesasRecorrentes.filter(d => !d.data_pagamento && d.id !== modalEditarDespesa.id).map(d => d.id) : []"
                      >
                    </th>
                    <th>Vencimento</th>
                    <th>Valor</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Estrutura principal da tabela com despesas e seus anexos/comprovantes embutidos -->
                  <template v-for="despesa in despesasRecorrentes" :key="`despesa-grupo-${despesa.id}`">
                    <!-- Linha principal da despesa -->
                    <tr :class="{ 'table-primary': despesa.editando }">
                      <td>
                        <input
                          v-if="!despesa.data_pagamento && despesa.id !== modalEditarDespesa.id"
                          type="checkbox"
                          class="form-check-input"
                          :checked="despesasSelecionadasParaRemocao.includes(despesa.id)"
                          @change="toggleSelecionarDespesaParaRemocao(despesa.id)"
                        >
                      </td>
                      <td>
                        <div v-if="despesa.editando">
                          <input
                            v-model="despesa.novoVencimento"
                            type="date"
                            class="form-control form-control-sm"
                            :min="dataMinima"
                          >
                        </div>
                        <div v-else>
                          {{ useStore().formatarData(despesa.vencimento) }}
                        </div>
                      </td>
                      <td>
                        <div v-if="despesa.editando">
                          <input
                            v-model="despesa.novoValor"
                            v-maska
                            type="text"
                            data-maska="##########,##"
                            data-maska-reversed
                            class="form-control form-control-sm"
                          >
                        </div>
                        <div v-else>
                          {{ useStore().formatarDinheiro(despesa.valor, true) }}
                        </div>
                      </td>
                      <td>
                        <CBadge :color="despesa.data_pagamento ? 'success' : 'warning'">
                          {{ despesa.data_pagamento ? 'Pago' : 'Pendente' }}
                        </CBadge>
                        <div v-if="despesa.comprovantes && despesa.comprovantes.length > 0" class="mt-1">
                          <span
                            class="badge rounded-pill bg-info cursor-pointer"
                            @click="despesa.mostrarComprovantes = !despesa.mostrarComprovantes"
                          >
                            <CIcon icon="cil-paperclip" class="me-1" />
                            {{ despesa.comprovantes.length }} anexo(s)
                          </span>
                        </div>
                      </td>
                      <td>
                        <!-- Ações para cada despesa recorrente -->
                        <div v-if="despesa.editando" class="btn-group">
                          <CButton
                            color="success"
                            size="sm"
                            @click="salvarEdicaoDespesaRecorrente(despesa)"
                          >
                            <CIcon icon="cil-check" />
                          </CButton>
                          <CButton
                            color="secondary"
                            size="sm"
                            @click="cancelarEdicaoDespesaRecorrente(despesa)"
                          >
                            <CIcon icon="cil-x" />
                          </CButton>
                        </div>
                        <div v-else class="btn-group">
                          <CButton
                            v-if="!despesa.data_pagamento"
                            color="primary"
                            size="sm"
                            @click="iniciarEdicaoDespesaRecorrente(despesa)"
                          >
                            <CIcon icon="cil-pencil" />
                          </CButton>
                          <CButton
                            color="info"
                            size="sm"
                            @click="abrirGerenciadorDeAnexos(despesa)"
                          >
                            <CIcon icon="cil-folder-open" />
                          </CButton>
                        </div>
                      </td>
                    </tr>

                    <!-- Linha de comprovantes para esta despesa específica -->
                    <tr v-if="despesa.mostrarComprovantes" class="comprovantes-row">
                      <td colspan="5">
                        <div class="comprovantes-list p-2 bg-light rounded">
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <h6 class="mb-0">
                              Comprovantes
                            </h6>
                            <CButton
                              color="secondary"
                              size="sm"
                              @click="despesa.mostrarComprovantes = false"
                            >
                              <CIcon icon="cil-x" />
                            </CButton>
                          </div>
                          <div class="list-group">
                            <template
                              v-for="comprovante in despesa.comprovantes"
                              :key="comprovante.id"
                            >
                              <div
                                v-if="comprovante.arquivo_tipo !== 'ARQUIVOS_MESCLADOS'"
                                class="list-group-item d-flex justify-content-between align-items-center comprovante-item"
                              >
                                <span
                                  class="text-primary comprovante-link"
                                  @click="store.downloadComprovante(despesa.id, comprovante.id)"
                                >
                                  <CIcon name="cil-cloud-download" class="me-2" />
                                  {{ comprovante.arquivo_tipo === 'COMPROVANTE_UNIFICADO' ? 'Comprovante Unificado' : comprovante.arquivo_nome }}
                                </span>
                                <button class="btn btn-danger btn-sm" @click.prevent="excluirComprovanteRecorrencia(despesa.id, comprovante.id)">
                                  <CIcon icon="cil-trash" />
                                </button>
                              </div>
                            </template>
                          </div>
                        </div>
                      </td>
                    </tr>

                    <!-- Linha do gerenciador de anexos para esta despesa específica -->
                    <tr v-if="despesa.gerenciandoAnexos" class="anexos-row">
                      <td colspan="5">
                        <div class="p-3 bg-light rounded">
                          <h6 class="mb-2">
                            Adicionar novos comprovantes
                          </h6>
                          <FilePond
                            ref="pondRecorrencia"
                            name="arquivosRecorrencia"
                            :files="arquivosRecorrencia[despesa.id] || []"
                            :label-idle="'Arraste e solte seus arquivos ou <u>clique aqui</u>'"
                            :allow-multiple="true"
                            :accepted-file-types="['application/pdf', 'image/*']"
                            @updatefiles="files => atualizarArquivosRecorrencia(despesa.id, files)"
                          />
                          <div class="d-flex justify-content-end mt-2">
                            <CButton
                              color="success"
                              size="sm"
                              @click="salvarArquivosRecorrencia(despesa)"
                            >
                              <CIcon icon="cil-save" />
                              Salvar
                            </CButton>
                            <CButton
                              color="secondary"
                              size="sm"
                              class="ms-2"
                              @click="fecharGerenciadorDeAnexos(despesa)"
                            >
                              <CIcon icon="cil-x" />
                              Cancelar
                            </CButton>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else class="text-center py-4">
            <p class="text-muted">
              Não foram encontradas outras despesas nesta série de recorrência.
            </p>
          </div>

          <!-- Interface para upload em massa -->
          <div v-if="mostraSeletorAnexosMassa" class="p-3 bg-light rounded">
            <h6 class="mb-2">
              Upload em Massa
            </h6>
            <FilePond
              ref="pondMassa"
              name="arquivosMassaRecorrencia"
              :files="arquivosMassaRecorrencia"
              :label-idle="'Arraste e solte seus arquivos ou <u>clique aqui</u>'"
              :allow-multiple="true"
              :accepted-file-types="['application/pdf', 'image/*']"
              @updatefiles="atualizarArquivosMassaRecorrencia"
            />
            <div class="table-responsive mt-3">
              <table class="table">
                <thead>
                  <tr>
                    <th style="width: 40px">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :checked="selecionadasParaAnexos.length === despesasRecorrentes.filter(d => !d.data_pagamento && d.id !== modalEditarDespesa.id).length && selecionadasParaAnexos.length > 0"
                        @change="selecionadasParaAnexos = $event.target.checked ? despesasRecorrentes.filter(d => !d.data_pagamento && d.id !== modalEditarDespesa.id).map(d => d.id) : []"
                      >
                    </th>
                    <th>Vencimento</th>
                    <th>Valor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="despesa in despesasRecorrentes" :key="despesa.id">
                    <td>
                      <input
                        v-if="!despesa.data_pagamento && despesa.id !== modalEditarDespesa.id"
                        type="checkbox"
                        class="form-check-input"
                        :checked="selecionadasParaAnexos.includes(despesa.id)"
                        @change="toggleSelecionarDespesaParaAnexos(despesa.id)"
                      >
                    </td>
                    <td>{{ useStore().formatarData(despesa.vencimento) }}</td>
                    <td>{{ useStore().formatarDinheiro(despesa.valor, true) }}</td>
                    <td>
                      <CBadge :color="despesa.data_pagamento ? 'success' : 'warning'">
                        {{ despesa.data_pagamento ? 'Pago' : 'Pendente' }}
                      </CBadge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="d-flex justify-content-end mt-2">
              <CButton
                color="success"
                size="sm"
                @click="salvarAnexosEmMassa"
              >
                <CIcon icon="cil-save" />
                Salvar
              </CButton>
              <CButton
                color="secondary"
                size="sm"
                class="ms-2"
                @click="fecharUploadEmMassa"
              >
                <CIcon icon="cil-x" />
                Cancelar
              </CButton>
            </div>
          </div>

          <CModalFooter>
            <CButton color="secondary" @click="cancelar">
              Fechar
            </CButton>
          </CModalFooter>
        </div>
      </CModalBody>
    </CModal>
  </div>
</template>

<style scoped>
label { font-weight: bold; }
.hover-highlight:hover {
  background-color: #f8f9fa;
}
.cursor-pointer {
  cursor: pointer;
}
:deep(.ck-content) { height:120px; }

.comprovante-link {
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-grow: 1;
  padding: 0.25rem;
}

.comprovante-link:hover {
  text-decoration: underline;
}

.comprovante-item {
  padding: 0.25rem 0.5rem;
  transition: background-color 0.2s;
}

.comprovante-item:hover {
  background-color: #f8f9fa;
}

/* Estilos para a aba de recorrência */
.table-responsive {
  max-height: 400px;
  overflow-y: auto;
}

.table-primary {
  transition: background-color 0.3s ease;
}

tr.table-primary td input.form-control {
  background-color: #fff;
}

.btn-group .btn {
  padding: 0.175rem 0.5rem;
}

/* Estilos para as linhas de comprovantes e anexos */
.comprovantes-row td,
.anexos-row td {
  padding: 0.5rem 0;
  background-color: #f8f9fa;
  border-top: none;
}

.comprovantes-list {
  border: 1px solid #dee2e6;
}

/* Badge clicável */
.badge.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s;
}

.badge.cursor-pointer:hover {
  opacity: 0.8;
}
</style>
